import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InfoOutlined as InfoIcon,
  PauseCircle as PauseIcon,
  PlayCircle as ResumeIcon,
} from '@mui/icons-material';
import { integrationsApi } from '../../services/api';

const WEBHOOK_BASE =
  process.env.NODE_ENV === 'production'
    ? 'https://nownextnotyet.syllamatic.com/api'
    : 'http://localhost:5000/api';

function CopyableField({ label, value }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <TextField
      label={label}
      value={value}
      fullWidth
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={copied ? 'Copied!' : 'Copy'}>
              <IconButton onClick={handleCopy} size="small">
                {copied ? <CheckIcon color="success" fontSize="small" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
      size="small"
    />
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString();
}

function SyncLogRow({ entry }) {
  const [open, setOpen] = useState(false);
  const hasNames = entry.imported_names && entry.imported_names.length > 0;
  const hasErrors = entry.errors && entry.errors.length > 0;

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>{formatDate(entry.synced_at)}</TableCell>
        <TableCell>
          <Chip
            label={entry.triggered_by}
            size="small"
            color={entry.triggered_by === 'manual' ? 'primary' : 'default'}
            variant="outlined"
          />
        </TableCell>
        <TableCell align="center">
          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            {entry.projects_created}
            {hasNames && (
              <Tooltip title={entry.imported_names.join(', ')}>
                <InfoIcon fontSize="small" color="action" sx={{ cursor: 'default' }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell align="center">{entry.projects_skipped}</TableCell>
        <TableCell align="center">
          {hasErrors ? (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              <Chip label={entry.errors.length} size="small" color="error" />
              {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Chip label="None" size="small" color="success" variant="outlined" />
          )}
        </TableCell>
      </TableRow>
      {hasErrors && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0 }}>
            <Collapse in={open}>
              <Box sx={{ py: 1 }}>
                {entry.errors.map((e, i) => (
                  <Typography key={i} variant="caption" display="block" color="error">
                    {e}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function IntegrationsTab() {
  const [settings, setSettings] = useState({
    email_webhook_secret: '',
    email_smtp_enabled: 'false',
    email_smtp_port: '2525',
    email_smtp_address: '',
    gitlab_url: '',
    gitlab_token: '',
    gitlab_project_ids: '',
    gitlab_group_ids: '',
    gitlab_sync_enabled: 'false',
    gitlab_sync_interval_hours: '2',
    gitlab_rule_max_age_days: '2',
    gitlab_rule_comment_days: '30',
    gitlab_rule_include_closed: 'false',
    gitlab_sync_paused: 'false',
  });

  const [pauseLoading, setPauseLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [syncLog, setSyncLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const fetchLog = useCallback(() => {
    setLogLoading(true);
    integrationsApi.getSyncLog(50)
      .then((res) => setSyncLog(res.data))
      .catch(() => {})
      .finally(() => setLogLoading(false));
  }, []);

  useEffect(() => {
    integrationsApi.getSettings().then((res) => {
      setSettings((prev) => ({ ...prev, ...res.data }));
    }).catch(() => {});
    fetchLog();
  }, [fetchLog]);

  const BOOL_FIELDS = new Set([
    'gitlab_sync_enabled',
    'gitlab_rule_include_closed',
    'email_smtp_enabled',
  ]);

  const handleChange = (field) => (e) => {
    const value = BOOL_FIELDS.has(field) ? (e.target.checked ? 'true' : 'false') : e.target.value;
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await integrationsApi.saveSettings(settings);
      setSaveMsg({ severity: 'success', text: 'Settings saved.' });
    } catch {
      setSaveMsg({ severity: 'error', text: 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestGitLab = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await integrationsApi.testGitLab();
      setTestResult(res.data);
    } catch (e) {
      setTestResult({ success: false, message: e.response?.data?.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await integrationsApi.syncGitLab();
      setSyncResult({ severity: 'info', text: res.data.message });
      // Sync runs in background — refresh log after a short delay, then again later
      setTimeout(() => fetchLog(), 5000);
      setTimeout(() => fetchLog(), 15000);
    } catch (e) {
      setSyncResult({ severity: 'error', text: e.response?.data?.error || 'Sync failed' });
      fetchLog();
    } finally {
      setSyncing(false);
    }
  };

  const handleTogglePause = async () => {
    const newPaused = settings.gitlab_sync_paused === 'true' ? 'false' : 'true';
    const updated = { ...settings, gitlab_sync_paused: newPaused };
    setPauseLoading(true);
    try {
      await integrationsApi.saveSettings(updated);
      setSettings(updated);
    } catch {
      // silently fail — user can retry via Save
    } finally {
      setPauseLoading(false);
    }
  };

  const webhookUrl = `${WEBHOOK_BASE}/integrations/email/inbound`;

  return (
    <Box>
      {/* ------------------------------------------------------------------ */}
      {/* Email Integration                                                   */}
      {/* ------------------------------------------------------------------ */}
      <Typography variant="h6" gutterBottom>
        Email Integration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Employees can create new projects by forwarding an email to a dedicated inbound address
        built into this app. The email body must contain the code <strong>201661</strong> and the
        sender must match an employee email or domain. The subject becomes the project name; the
        body becomes the description.
      </Typography>

      {/* Direct SMTP (primary method) */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Direct inbound email address
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The app listens directly for incoming email — no third-party service needed. Employees
        just forward to the address below.
      </Typography>

      <Stack spacing={2} sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={settings.email_smtp_enabled === 'true'}
              onChange={handleChange('email_smtp_enabled')}
            />
          }
          label="Enable direct SMTP listener"
        />
        <TextField
          label="Inbound email address"
          placeholder="projects@yourdomain.com"
          value={settings.email_smtp_address}
          onChange={handleChange('email_smtp_address')}
          fullWidth
          size="small"
          helperText="The address employees forward to. Must match the MX record you configure in DNS."
        />
        <TextField
          label="SMTP listener port"
          type="number"
          value={settings.email_smtp_port}
          onChange={handleChange('email_smtp_port')}
          size="small"
          sx={{ width: 200 }}
          helperText="Default 2525. Port 25 requires root or a redirect (see below)."
        />
      </Stack>

      {settings.email_smtp_address && settings.email_smtp_enabled === 'true' && (
        <Box sx={{ mb: 2 }}>
          <CopyableField
            label="Forward emails to this address"
            value={settings.email_smtp_address}
          />
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          One-time server setup
        </Typography>
        <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
          <li>
            Enter the inbound address above (e.g. <code>projects@nownextnotyet.syllamatic.com</code>)
            and enable the listener.
          </li>
          <li>
            <strong>Save settings</strong> and restart the service so the listener binds the port:{' '}
            <code>sudo systemctl restart nownextnotyet-backend</code>
          </li>
          <li>
            In your DNS, add an <strong>MX record</strong> pointing to this server's hostname or IP.
            Example: <code>nownextnotyet.syllamatic.com IN MX 10 nownextnotyet.syllamatic.com</code>
          </li>
          <li>
            Open the configured port in your firewall/security group (e.g. port 2525 inbound TCP).
          </li>
          <li>
            <em>If using port 2525 instead of 25:</em> sending mail servers always connect on port 25.
            Redirect port 25 → 2525 on the server:{' '}
            <code>
              {`sudo iptables -t nat -A PREROUTING -p tcp --dport 25 -j REDIRECT --to-port ${settings.email_smtp_port || '2525'}`}
            </code>
            {' '}(add to <code>/etc/iptables/rules.v4</code> to persist across reboots).
            Alternatively, use port 25 directly after granting the capability:{' '}
            <code>sudo setcap cap_net_bind_service=+ep $(which python3)</code>
          </li>
          <li>
            Employees forward any email to the address above, placing <strong>201661</strong> somewhere
            in the message body (e.g. as the first line of their forward note).
          </li>
        </Typography>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Webhook method (alternative) */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Alternative: Webhook (Mailgun / SendGrid / Postmark)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        If you prefer to route email through a third-party provider, configure an inbound route
        that POSTs to the URL below with the webhook secret as an HTTP header.
      </Typography>

      <Stack spacing={2} sx={{ mb: 2 }}>
        <CopyableField label="Webhook URL" value={webhookUrl} />
        <CopyableField
          label="Webhook Secret (X-Webhook-Secret header)"
          value={settings.email_webhook_secret || '(not yet loaded)'}
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          Webhook setup (Mailgun / SendGrid / Postmark)
        </Typography>
        <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
          <li>In your email service, create an inbound route that forwards to the Webhook URL above.</li>
          <li>
            Add the HTTP header <code>X-Webhook-Secret: &lt;secret&gt;</code> to every forwarded
            request so Now|Next|NotYet can verify the source.
          </li>
          <li>
            Employees forward any email to the inbound address, placing <strong>201661</strong>{' '}
            somewhere in the message body.
          </li>
        </Typography>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* ------------------------------------------------------------------ */}
      {/* GitLab Integration                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Typography variant="h6" gutterBottom>
        GitLab Integration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Now|Next|NotYet polls GitLab for new or recently-active issues and creates potential projects
        automatically. Configure the connection, import rules, and sync schedule below.
      </Typography>

      {/* Connection */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Connection
      </Typography>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="GitLab Instance URL"
          placeholder="https://gitlab.example.com"
          value={settings.gitlab_url}
          onChange={handleChange('gitlab_url')}
          fullWidth
          size="small"
          helperText="Leave blank to use gitlab.com. Include the protocol (https://)."
        />

        <TextField
          label="Personal Access Token"
          value={settings.gitlab_token}
          onChange={handleChange('gitlab_token')}
          type={showToken ? 'text' : 'password'}
          fullWidth
          size="small"
          helperText="Needs read_api scope for syncing; api scope to also push owner changes back to GitLab as assignee updates."
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowToken(!showToken)} size="small">
                  {showToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="GitLab Project IDs or Paths (one per line or comma-separated)"
          value={settings.gitlab_project_ids}
          onChange={handleChange('gitlab_project_ids')}
          multiline
          minRows={2}
          fullWidth
          size="small"
          placeholder={'123\nmygroup/myproject'}
          helperText="Specific projects only. Use the numeric project ID or full path (e.g. mygroup/myproject)."
        />

        <TextField
          label="GitLab Groups (one per line or comma-separated)"
          value={settings.gitlab_group_ids}
          onChange={handleChange('gitlab_group_ids')}
          multiline
          minRows={2}
          fullWidth
          size="small"
          placeholder={'mygroup\n456'}
          helperText="All projects in each group (and subgroups) will be synced. Use the group path or numeric ID."
        />
      </Stack>

      {/* Import Rules */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Import Rules
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          An issue is imported when it matches either rule:
          <br />
          <strong>Rule 1 — New issue:</strong> created within the last <em>N</em> days (always imported regardless of state).
          <br />
          <strong>Rule 2 — Active issue:</strong> older than Rule 1 threshold, not closed*, and has received a comment within the last <em>M</em> days.
          <br />
          <Typography variant="caption" color="text.secondary">* Unless "Include closed issues" is enabled.</Typography>
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <TextField
            label="New issue threshold (days)"
            type="number"
            value={settings.gitlab_rule_max_age_days}
            onChange={handleChange('gitlab_rule_max_age_days')}
            size="small"
            inputProps={{ min: 1, max: 365 }}
            sx={{ width: 220 }}
            helperText="Rule 1: issues newer than this are always imported"
          />
          <TextField
            label="Recent comment window (days)"
            type="number"
            value={settings.gitlab_rule_comment_days}
            onChange={handleChange('gitlab_rule_comment_days')}
            size="small"
            inputProps={{ min: 1, max: 365 }}
            sx={{ width: 220 }}
            helperText="Rule 2: must have a comment within this many days"
          />
          <FormControlLabel
            control={
              <Switch
                checked={settings.gitlab_rule_include_closed === 'true'}
                onChange={handleChange('gitlab_rule_include_closed')}
              />
            }
            label="Include closed issues (Rule 2)"
            sx={{ mt: 1 }}
          />
        </Stack>
      </Paper>

      {/* Sync Schedule */}
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
        Sync Schedule
      </Typography>

      {settings.gitlab_sync_paused === 'true' && (
        <Alert
          severity="warning"
          icon={<PauseIcon />}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<ResumeIcon />}
              onClick={handleTogglePause}
              disabled={pauseLoading}
            >
              Resume
            </Button>
          }
        >
          Scheduled syncs are <strong>paused</strong>. New issues will not be retrieved automatically until you resume.
        </Alert>
      )}

      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ mb: 3 }}>
        <TextField
          label="Auto-sync interval (hours)"
          type="number"
          value={settings.gitlab_sync_interval_hours}
          onChange={handleChange('gitlab_sync_interval_hours')}
          size="small"
          inputProps={{ min: 1, max: 168 }}
          sx={{ width: 200 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.gitlab_sync_enabled === 'true'}
              onChange={handleChange('gitlab_sync_enabled')}
            />
          }
          label="Enable automatic background sync"
        />
        <Button
          variant="outlined"
          color={settings.gitlab_sync_paused === 'true' ? 'success' : 'warning'}
          size="small"
          startIcon={pauseLoading
            ? <CircularProgress size={16} />
            : settings.gitlab_sync_paused === 'true' ? <ResumeIcon /> : <PauseIcon />}
          onClick={handleTogglePause}
          disabled={pauseLoading || settings.gitlab_sync_enabled !== 'true'}
          sx={{ height: 36 }}
        >
          {settings.gitlab_sync_paused === 'true' ? 'Resume Sync' : 'Pause Sync'}
        </Button>
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleTestGitLab}
          disabled={testing}
          startIcon={testing ? <CircularProgress size={16} /> : null}
        >
          Test Connection
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSyncNow}
          disabled={syncing}
          startIcon={syncing ? <CircularProgress size={16} /> : <RefreshIcon />}
        >
          Sync Now
        </Button>
      </Stack>

      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          icon={testResult.success ? <CheckIcon /> : <ErrorIcon />}
          sx={{ mb: 2 }}
          onClose={() => setTestResult(null)}
        >
          {testResult.message}
        </Alert>
      )}

      {syncResult && (
        <Alert severity={syncResult.severity} sx={{ mb: 2 }} onClose={() => setSyncResult(null)}>
          {syncResult.text}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          How to create a GitLab Personal Access Token
        </Typography>
        <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
          <li>
            Sign in to your GitLab instance and go to{' '}
            <strong>User menu → Edit profile → Access Tokens</strong>.
          </li>
          <li>
            Click <strong>Add new token</strong>, give it a name (e.g.{' '}
            <em>Now|Next|NotYet integration</em>), set an expiry, and select the{' '}
            <strong>read_api</strong> scope.
          </li>
          <li>Copy the generated token and paste it in the field above.</li>
          <li>
            Find each project's ID in <strong>GitLab → Project → Settings → General</strong>{' '}
            (shown at the top of the page), or use the full path visible in the browser URL.
          </li>
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          Optional: set up a system cron job for more reliable syncing
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          The background scheduler runs inside the app process. For guaranteed periodic syncing,
          add a cron entry on the server (runs every 2 hours):
        </Typography>
        <Box
          component="code"
          sx={{
            display: 'block',
            bgcolor: 'grey.100',
            p: 1,
            borderRadius: 1,
            fontSize: '0.8rem',
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}
        >
          {`0 */2 * * * curl -s -X POST ${WEBHOOK_BASE}/integrations/gitlab/sync > /dev/null 2>&1`}
        </Box>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* ------------------------------------------------------------------ */}
      {/* Import Log                                                          */}
      {/* ------------------------------------------------------------------ */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Import Log</Typography>
        <IconButton size="small" onClick={fetchLog} disabled={logLoading}>
          {logLoading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        History of GitLab sync runs. Hover the <InfoIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} /> icon on a Created count to see which projects were imported.
      </Typography>

      {syncLog.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No syncs have run yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell align="center">Created</TableCell>
                <TableCell align="center">Skipped</TableCell>
                <TableCell align="center">Errors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {syncLog.map((entry) => (
                <SyncLogRow key={entry.id} entry={entry} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider sx={{ my: 3 }} />

      {saveMsg && (
        <Alert severity={saveMsg.severity} sx={{ mb: 2 }} onClose={() => setSaveMsg(null)}>
          {saveMsg.text}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save Integration Settings
        </Button>
      </Box>
    </Box>
  );
}

export default IntegrationsTab;
